import { useCallback, useEffect, useRef, useState } from "react"
import { useSetRecoilState } from "recoil"
import {
  paintCircularAudioVisualizer,
  resetCircularAudioVisualizerEnvelope,
} from "../utils/circularAudioVisualizer"
import {
  createTranscriptAudioSession,
  attachTranscriptAudioToSession,
  fetchTranscriptSessionDetail,
  formatDefaultTranscriptTitle,
  TranscriptSessionModality,
} from "../utils/transcriptApi"
import { transcriptRecordingState } from "../utils/store"

interface UseTranscriptRecorderOptions {
  clientId: string
  firstName?: string | null
  lastName?: string | null
  practitionerId: string
  jwt: string
  /** When set, the next completed recording attaches to this session. */
  attachToSessionId?: string | null
  onSessionCreated?: (sessionId: string) => void
  onSessionProcessingComplete?: (sessionId: string) => void | Promise<void>
}

/**
 * Manages microphone recording and transcript upload independently of tab state.
 */
export default function useTranscriptRecorder({
  clientId,
  firstName,
  lastName,
  practitionerId,
  jwt,
  attachToSessionId = null,
  onSessionCreated,
  onSessionProcessingComplete,
}: UseTranscriptRecorderOptions) {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [processingSessionId, setProcessingSessionId] = useState<string | null>(
    null
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const setIsTranscriptRecording = useSetRecoilState(transcriptRecordingState)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  const recordedElapsedMsRef = useRef(0)
  const recordingStartedAtRef = useRef<number | null>(null)
  const recordingStartedAtUtcRef = useRef<Date | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  const wakeLockRequestRef = useRef<Promise<WakeLockSentinel> | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const audioMeterFillRef = useRef<HTMLDivElement | null>(null)
  const audioVisualizerCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const elapsedTimeElementsRef = useRef(new Set<HTMLElement>())
  const isAudioLevelUpdatingRef = useRef(false)
  const shouldUploadOnStopRef = useRef(false)
  const recordingModalityRef = useRef<TranscriptSessionModality>("in_person")
  const attachToSessionIdRef = useRef<string | null>(attachToSessionId ?? null)
  attachToSessionIdRef.current = attachToSessionId ?? null
  const isProcessing = processingSessionId !== null

  const getElapsedSeconds = useCallback(() => {
    const activeSegmentElapsedMs = recordingStartedAtRef.current
      ? Date.now() - recordingStartedAtRef.current
      : 0
    return Math.floor(
      (recordedElapsedMsRef.current + activeSegmentElapsedMs) / 1000
    )
  }, [])

  const updateElapsedTime = useCallback(() => {
    const elapsedTime = formatDuration(getElapsedSeconds())
    elapsedTimeElementsRef.current.forEach((element) => {
      element.textContent = elapsedTime
    })
  }, [getElapsedSeconds])

  const registerElapsedTimeElement = useCallback(
    (element: HTMLElement) => {
      elapsedTimeElementsRef.current.add(element)
      element.textContent = formatDuration(getElapsedSeconds())

      return () => {
        elapsedTimeElementsRef.current.delete(element)
      }
    },
    [getElapsedSeconds]
  )

  const stopElapsedTimer = useCallback(() => {
    if (recordingStartedAtRef.current) {
      recordedElapsedMsRef.current += Date.now() - recordingStartedAtRef.current
      recordingStartedAtRef.current = null
    }

    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }

    updateElapsedTime()
  }, [updateElapsedTime])

  const startElapsedTimer = useCallback(() => {
    recordingStartedAtRef.current = Date.now()
    updateElapsedTime()
    timerRef.current = window.setInterval(updateElapsedTime, 1000)
  }, [updateElapsedTime])

  const resetElapsedTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
    recordedElapsedMsRef.current = 0
    recordingStartedAtRef.current = null
    updateElapsedTime()
  }, [updateElapsedTime])

  const updateAudioLevel = useCallback(() => {
    if (!isAudioLevelUpdatingRef.current || !analyserRef.current) {
      return
    }

    const audioContext = audioContextRef.current
    if (audioContext?.state === "suspended") {
      void audioContext.resume().catch(() => {
        // Metering is best-effort.
      })
    }

    const analyser = analyserRef.current
    const frequency = new Uint8Array(analyser.frequencyBinCount)
    const timeDomain = new Uint8Array(analyser.fftSize)
    analyser.getByteFrequencyData(frequency)
    analyser.getByteTimeDomainData(timeDomain)

    let peakLevel = 0
    for (let i = 0; i < frequency.length; i += 1) {
      peakLevel = Math.max(peakLevel, frequency[i] ?? 0)
    }
    for (let i = 0; i < timeDomain.length; i += 1) {
      peakLevel = Math.max(
        peakLevel,
        Math.round((Math.abs((timeDomain[i] ?? 128) - 128) / 128) * 255)
      )
    }

    if (audioMeterFillRef.current) {
      audioMeterFillRef.current.style.width = `${Math.min(
        (peakLevel / 255) * 100,
        100
      )}%`
    }

    const paused = mediaRecorderRef.current?.state === "paused"
    const paintOptions = { paused, timeDomainData: timeDomain }
    // Paint every mounted visualizer. LoopShell keeps a desktop sidebar and a
    // mobile drawer sidebar mounted; a single ref can point at a display:none
    // canvas while the visible ring stays idle.
    const canvases = new Set<HTMLCanvasElement>()
    if (audioVisualizerCanvasRef.current) {
      canvases.add(audioVisualizerCanvasRef.current)
    }
    document
      .querySelectorAll<HTMLCanvasElement>(
        "canvas[data-audio-visualizer='circular']"
      )
      .forEach((canvas) => canvases.add(canvas))
    canvases.forEach((canvas) => {
      paintCircularAudioVisualizer(canvas, frequency, paintOptions)
    })

    animationFrameRef.current = window.requestAnimationFrame(updateAudioLevel)
  }, [])

  const startAudioLevelUpdates = useCallback(() => {
    resetCircularAudioVisualizerEnvelope()
    isAudioLevelUpdatingRef.current = true
    updateAudioLevel()
  }, [updateAudioLevel])

  const stopAudioLevelUpdates = useCallback(() => {
    isAudioLevelUpdatingRef.current = false
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    if (audioMeterFillRef.current) {
      audioMeterFillRef.current.style.width = "0%"
    }
    const canvas = audioVisualizerCanvasRef.current
    if (canvas) {
      paintCircularAudioVisualizer(canvas, new Uint8Array(64), { paused: true })
    }
  }, [])

  const requestWakeLock = useCallback(async () => {
    if (
      !navigator.wakeLock ||
      document.visibilityState !== "visible" ||
      (wakeLockRef.current && !wakeLockRef.current.released) ||
      wakeLockRequestRef.current
    ) {
      return
    }

    const request = navigator.wakeLock.request("screen")
    wakeLockRequestRef.current = request

    try {
      const wakeLock = await request
      const isCurrentRequest = wakeLockRequestRef.current === request
      if (isCurrentRequest) {
        wakeLockRequestRef.current = null
      }
      if (
        !isCurrentRequest ||
        document.visibilityState !== "visible" ||
        !["recording", "paused"].includes(
          mediaRecorderRef.current?.state ?? "inactive"
        )
      ) {
        void wakeLock.release().catch(() => {
          // The browser may have already released the stale lock.
        })
        return
      }

      wakeLockRef.current = wakeLock
    } catch {
      if (wakeLockRequestRef.current === request) {
        wakeLockRequestRef.current = null
      }
      // Wake lock is best effort; recording can continue if the browser denies it.
    }
  }, [])

  const releaseWakeLock = useCallback(() => {
    wakeLockRequestRef.current = null
    const wakeLock = wakeLockRef.current
    wakeLockRef.current = null

    if (!wakeLock || wakeLock.released) {
      return
    }

    void wakeLock.release().catch(() => {
      // The browser may have already released the lock during a visibility change.
    })
  }, [])

  const stopRecordingResources = useCallback(() => {
    stopElapsedTimer()
    stopAudioLevelUpdates()
    releaseWakeLock()
    setIsTranscriptRecording(false)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (audioContextRef.current) {
      void audioContextRef.current.close()
      audioContextRef.current = null
    }
    analyserRef.current = null
    mediaRecorderRef.current = null
    setIsPaused(false)
  }, [
    releaseWakeLock,
    setIsTranscriptRecording,
    stopAudioLevelUpdates,
    stopElapsedTimer,
  ])

  useEffect(() => {
    return () => {
      stopRecordingResources()
    }
  }, [stopRecordingResources])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        ["recording", "paused"].includes(
          mediaRecorderRef.current?.state ?? "inactive"
        )
      ) {
        void requestWakeLock()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [requestWakeLock])

  useEffect(() => {
    if (!processingSessionId) {
      return undefined
    }

    let ignore = false
    let reachedTerminalStatus = false
    let intervalId: number | null = null

    const stopPolling = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId)
        intervalId = null
      }
    }

    const pollProcessingStatus = async () => {
      try {
        const detail = await fetchTranscriptSessionDetail(
          jwt,
          processingSessionId
        )

        if (ignore || reachedTerminalStatus) {
          return
        }

        if (detail.session.status === "complete") {
          reachedTerminalStatus = true
          stopPolling()
          setProcessingSessionId((current) =>
            current === processingSessionId ? null : current
          )
          await onSessionProcessingComplete?.(processingSessionId)
          return
        }

        if (detail.session.status === "error") {
          reachedTerminalStatus = true
          stopPolling()
          setProcessingSessionId((current) =>
            current === processingSessionId ? null : current
          )
          setErrorMessage(
            detail.session.errorMessage || "Transcript processing failed."
          )
        }
      } catch {
        // Processing-status fetches can fail transiently; retry on the next poll.
      }
    }

    void pollProcessingStatus()
    intervalId = window.setInterval(() => {
      void pollProcessingStatus()
    }, 3000)

    return () => {
      ignore = true
      stopPolling()
    }
  }, [jwt, onSessionProcessingComplete, processingSessionId])

  const startRecording = useCallback(
    async (modality: TranscriptSessionModality) => {
      if (isUploading || isProcessing) {
        return false
      }

      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        setErrorMessage("A microphone recording is already in progress.")
        return false
      }

      setErrorMessage(null)
      recordingModalityRef.current = modality

      if (!navigator.mediaDevices?.getUserMedia) {
        setErrorMessage("This browser does not support microphone recording.")
        return false
      }

      try {
        // Note, we may need to fall back on a different audio configuration if the user's device doesn't support the requested configuration. For now, we will just use the default configuration.
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            // AGC lifts room noise into the visualizer and keeps the ring “loud”
            // even when the speaker is silent.
            autoGainControl: false,
            channelCount: 1,
            sampleRate: 48000,
          },
        })

        streamRef.current = stream

        const AudioContextCtor =
          window.AudioContext ||
          ((window as Window & { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext as typeof AudioContext | undefined)

        if (AudioContextCtor) {
          const audioContext = new AudioContextCtor()
          audioContextRef.current = audioContext
          const source = audioContext.createMediaStreamSource(stream)
          const analyser = audioContext.createAnalyser()
          analyser.fftSize = 2048
          analyser.smoothingTimeConstant = 0.12
          analyser.minDecibels = -100
          analyser.maxDecibels = -20
          source.connect(analyser)
          // Keep the graph active in browsers that stall disconnected analysers.
          const silent = audioContext.createGain()
          silent.gain.value = 0
          analyser.connect(silent)
          silent.connect(audioContext.destination)
          analyserRef.current = analyser
          if (audioContext.state === "suspended") {
            await audioContext.resume().catch(() => {
              // Metering is best-effort; recording can continue without it.
            })
          }
          // Resume again after await boundaries — Chrome often re-suspends
          // AudioContext when created after the original click gesture.
          if (audioContext.state === "suspended") {
            await audioContext.resume().catch(() => undefined)
          }
        }

        const mimeType = [
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/mp4;codecs=mp4a.40.2",
          "audio/mp4",
        ].find((candidate) => MediaRecorder.isTypeSupported(candidate))

        const mediaRecorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream)
        audioChunksRef.current = []
        shouldUploadOnStopRef.current = false
        mediaRecorderRef.current = mediaRecorder

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data)
          }
        }

        mediaRecorder.onerror = () => {
          setErrorMessage("The microphone recording stopped unexpectedly.")
          stopRecordingResources()
          resetElapsedTimer()
          setIsRecording(false)
        }

        mediaRecorder.onstop = async () => {
          const shouldUpload = shouldUploadOnStopRef.current
          shouldUploadOnStopRef.current = false
          const recordingModality = recordingModalityRef.current
          const startedAt = recordingStartedAtUtcRef.current ?? new Date()
          const endedAt = new Date()
          recordingStartedAtUtcRef.current = null
          const mimeExtension = mimeType?.includes("mp4") ? ".m4a" : ".webm"
          const recordedBlob = new Blob(audioChunksRef.current, {
            type: mimeType,
          })

          stopRecordingResources()
          setIsRecording(false)
          resetElapsedTimer()

          if (!shouldUpload) {
            return
          }

          if (recordedBlob.size === 0) {
            setErrorMessage("No audio was captured. Please try again.")
            return
          }

          setIsUploading(true)
          let createdSession: { sessionId: string }
          try {
            const attachSessionId = attachToSessionIdRef.current
            if (attachSessionId) {
              createdSession = await attachTranscriptAudioToSession(
                jwt,
                attachSessionId,
                {
                  audioBlob: recordedBlob,
                  filename: `recording${mimeExtension}`,
                  startedAt: startedAt.toISOString(),
                  endedAt: endedAt.toISOString(),
                  modality: recordingModality,
                  title: formatDefaultTranscriptTitle(
                    firstName,
                    lastName,
                    startedAt
                  ),
                }
              )
            } else {
              createdSession = await createTranscriptAudioSession(jwt, {
                audioBlob: recordedBlob,
                filename: `recording${mimeExtension}`,
                clientId,
                practitionerId,
                startedAt: startedAt.toISOString(),
                endedAt: endedAt.toISOString(),
                modality: recordingModality,
                title: formatDefaultTranscriptTitle(
                  firstName,
                  lastName,
                  startedAt
                ),
              })
            }
          } catch (error) {
            setErrorMessage(
              getErrorMessage(error, "Unable to upload recording.")
            )
            return
          } finally {
            setIsUploading(false)
            setIsPaused(false)
          }

          setProcessingSessionId(createdSession.sessionId)
          try {
            onSessionCreated?.(createdSession.sessionId)
          } catch {
            setErrorMessage(
              "The recording was uploaded, but the transcript session could not be refreshed."
            )
          }
        }

        mediaRecorder.start(1000)
        recordingStartedAtUtcRef.current = new Date()
        resetElapsedTimer()
        startElapsedTimer()
        startAudioLevelUpdates()
        void requestWakeLock()
        setIsRecording(true)
        setIsTranscriptRecording(true)
        setIsPaused(false)
        return true
      } catch (error) {
        recordingStartedAtUtcRef.current = null
        setErrorMessage(getMicrophoneErrorMessage(error))
        stopRecordingResources()
        resetElapsedTimer()
        return false
      }
    },
    [
      clientId,
      firstName,
      isProcessing,
      isUploading,
      jwt,
      lastName,
      onSessionCreated,
      practitionerId,
      resetElapsedTimer,
      requestWakeLock,
      setIsTranscriptRecording,
      startAudioLevelUpdates,
      startElapsedTimer,
      stopRecordingResources,
    ]
  )

  const pauseRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current
    if (mediaRecorder?.state !== "recording") {
      return
    }

    try {
      mediaRecorder.pause()
      stopElapsedTimer()
      stopAudioLevelUpdates()
      setIsPaused(true)
    } catch {
      setErrorMessage("Unable to pause the microphone recording.")
    }
  }, [stopAudioLevelUpdates, stopElapsedTimer])

  const resumeRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current
    if (mediaRecorder?.state !== "paused") {
      return
    }

    try {
      mediaRecorder.resume()
      startElapsedTimer()
      startAudioLevelUpdates()
      void requestWakeLock()
      setIsTranscriptRecording(true)
      setIsPaused(false)
    } catch {
      setErrorMessage("Unable to resume the microphone recording.")
    }
  }, [
    requestWakeLock,
    setIsTranscriptRecording,
    startAudioLevelUpdates,
    startElapsedTimer,
  ])

  const stopRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current
    if (
      mediaRecorder?.state === "recording" ||
      mediaRecorder?.state === "paused"
    ) {
      try {
        stopElapsedTimer()
        stopAudioLevelUpdates()
        shouldUploadOnStopRef.current = true
        mediaRecorder.stop()
      } catch {
        shouldUploadOnStopRef.current = false
        setErrorMessage("Unable to stop the microphone recording.")
      }
      return
    }

    stopRecordingResources()
    resetElapsedTimer()
    setIsRecording(false)
  }, [
    resetElapsedTimer,
    stopAudioLevelUpdates,
    stopElapsedTimer,
    stopRecordingResources,
  ])

  const clearError = useCallback(() => {
    setErrorMessage(null)
  }, [])

  return {
    state: {
      isRecording,
      isPaused,
      isUploading,
      isProcessing,
      errorMessage,
    },
    audioMeterFillRef,
    audioVisualizerCanvasRef,
    registerElapsedTimeElement,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    clearError,
  }
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0
    ? error.message
    : fallback
}

function getMicrophoneErrorMessage(error: unknown): string {
  if (!(error instanceof DOMException)) {
    return "Unable to access the microphone. Check your browser permissions."
  }

  switch (error.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone access is blocked. Allow it in your browser settings and try again."
    case "NotFoundError":
      return "No microphone was found. Connect or select a microphone and try again."
    case "NotReadableError":
    case "AbortError":
      return "The microphone is unavailable. Close other apps using it and try again."
    default:
      return "Unable to access the microphone. Check your browser permissions."
  }
}

function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`
}
